import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Loader2, LogOut, Star, FileText, Paperclip, Target, Activity, Flame } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, AreaChart, Area } from "recharts";
import { useClientAuth } from "@/context/ClientAuth";
import { toast } from "sonner";
import Attachment from "@/components/Attachment";
import Reschedule from "@/components/Reschedule";
import SkillWheel from "@/components/SkillWheel";
import { IDP_FIELDS } from "@/lib/idpFields";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BADGES = [
  { weeks: 4, label: "Rising Star", color: "#38BDF8" },
  { weeks: 8, label: "On Fire", color: "#2E6BE6" },
  { weeks: 12, label: "Elite Streak", color: "#D4AF37" },
];

export default function Dashboard() {
  const { user, token, logout } = useClientAuth();
  const nav = useNavigate();
  const [reports, setReports] = useState([]);
  const [idp, setIdp] = useState(null);
  const [skills, setSkills] = useState([]);
  const [skillCats, setSkillCats] = useState([]);
  const [maxRating, setMaxRating] = useState(7);
  const [selectedCat, setSelectedCat] = useState(null);
  const [fitness, setFitness] = useState([]);
  const [skillHistory, setSkillHistory] = useState([]);
  const [challenge, setChallenge] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user === false) nav("/login");
  }, [user, nav]);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/reports/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setReports(r.data.reports || []))
      .catch(() => {})
      .finally(() => setLoading(false));
    axios
      .get(`${API}/idp/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setIdp(r.data.idp))
      .catch(() => {});
    axios
      .get(`${API}/skills/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { setSkills(r.data.skills || []); setSkillCats(r.data.categories || []); setMaxRating(r.data.max_rating || 7); })
      .catch(() => {});
    axios
      .get(`${API}/fitness/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setFitness(r.data.entries || []))
      .catch(() => {});
    axios
      .get(`${API}/skills/history/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setSkillHistory(r.data.history || []))
      .catch(() => {});
    axios
      .get(`${API}/challenges/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setChallenge(r.data))
      .catch(() => {});
  }, [token]);

  const skillProgress = skillHistory.map((h) => ({ date: h.date?.slice(5), avg: h.average }));

  const chartData = [...reports]
    .filter((r) => typeof r.rating === "number")
    .sort((a, b) => (a.date > b.date ? 1 : -1))
    .map((r) => ({ date: r.date?.slice(5), rating: r.rating }));

  const totalSessions = reports.length;
  const ratedReports = reports.filter((r) => typeof r.rating === "number");
  const avgRating = ratedReports.length
    ? (ratedReports.reduce((s, r) => s + r.rating, 0) / ratedReports.length).toFixed(1)
    : null;
  const latestRating = chartData.length ? chartData[chartData.length - 1].rating : null;

  const completeChallenge = async () => {
    setCompleting(true);
    try {
      const { data } = await axios.post(`${API}/challenges/me/complete`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setChallenge((p) => ({ ...p, streak: data.streak, best_streak: data.best_streak, total_completed: data.total_completed, current: { ...p.current, completed: true } }));
      const hit = BADGES.find((b) => b.weeks === data.streak);
      if (hit) toast.success(`🏅 Badge unlocked: ${hit.label} — ${hit.weeks}-week streak!`);
      else toast.success(`Nice work! Streak: ${data.streak} week${data.streak === 1 ? "" : "s"} 🔥`);
    } catch {
      toast.error("Could not mark complete");
    } finally {
      setCompleting(false);
    }
  };

  if (user === null || user === false) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="client-dashboard">
      <header className="border-b border-white/10 px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="font-display font-bold uppercase tracking-tight text-xl">JG<span className="text-[#2E6BE6]">/</span>Coaching</Link>
        <div className="flex items-center gap-6">
          <Link to="/#book" className="text-xs uppercase tracking-[0.2em] text-white/60 link-underline hover:text-white">Book a session</Link>
          <button data-testid="client-logout" onClick={() => { logout(); nav("/"); }} className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/60 hover:text-white">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-12 md:py-16">
        <span className="text-[#2E6BE6] text-xs uppercase tracking-[0.3em]">Your progress</span>
        <h1 className="font-display font-bold uppercase tracking-tighter text-4xl md:text-6xl mt-3 leading-[0.9]">
          Hi {user.name?.split(" ")[0]}.
        </h1>
        <p className="text-white/50 mt-4 font-body">Every session report from your coach lives here.</p>

        {challenge && (challenge.current || challenge.total_completed > 0) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-10 border border-[#2E6BE6]/30 bg-gradient-to-br from-[#2E6BE6]/10 to-transparent p-6 md:p-8"
            data-testid="challenge-card"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-[240px]">
                <p className="text-[#2E6BE6] text-xs uppercase tracking-[0.3em] mb-3">This week's challenge</p>
                {challenge.current ? (
                  <>
                    <h3 className="font-display font-bold text-2xl md:text-3xl text-white">{challenge.current.title}</h3>
                    <p className="text-white/70 mt-2 text-sm leading-relaxed max-w-xl">{challenge.current.description}</p>
                    <button
                      data-testid="challenge-complete-btn"
                      onClick={completeChallenge}
                      disabled={completing || challenge.current.completed}
                      className={`mt-5 px-6 py-3 text-xs uppercase tracking-[0.15em] font-semibold flex items-center gap-2 transition-transform ${
                        challenge.current.completed
                          ? "bg-green-500/20 text-green-400 border border-green-400/40 cursor-default"
                          : "bg-[#D4AF37] text-black hover:-translate-y-0.5 disabled:opacity-50"
                      }`}
                    >
                      {challenge.current.completed ? "✓ Completed this week" : completing ? "Saving…" : "Mark as done"}
                    </button>
                  </>
                ) : (
                  <p className="text-white/60 text-sm">No challenge set for this week yet — your coach will send one soon. Keep that streak ready!</p>
                )}
              </div>
              <div className="flex gap-3">
                <div className="text-center border border-[#D4AF37]/30 px-5 py-4" data-testid="streak-current">
                  <Flame className="w-5 h-5 text-[#D4AF37] mx-auto" />
                  <p className="font-display font-bold text-3xl text-[#D4AF37] mt-1">{challenge.streak || 0}</p>
                  <p className="text-[10px] uppercase tracking-[0.15em] text-white/40">Week streak</p>
                </div>
                <div className="text-center border border-white/10 px-5 py-4" data-testid="streak-total">
                  <Star className="w-5 h-5 text-white/60 mx-auto" />
                  <p className="font-display font-bold text-3xl text-white mt-1">{challenge.total_completed || 0}</p>
                  <p className="text-[10px] uppercase tracking-[0.15em] text-white/40">Total done</p>
                </div>
              </div>
            </div>
            {challenge.best_streak > 0 && (
              <p className="text-white/30 text-[11px] mt-4">Best streak: {challenge.best_streak} weeks</p>
            )}
            <div className="flex flex-wrap gap-3 mt-5 pt-5 border-t border-white/10" data-testid="streak-badges">
              {BADGES.map((b) => {
                const earned = (challenge.best_streak || 0) >= b.weeks;
                return (
                  <div key={b.weeks} data-testid={`badge-${b.weeks}${earned ? "-earned" : "-locked"}`}
                    className={`flex items-center gap-2 px-3 py-2 border transition-all ${earned ? "border-white/20" : "border-white/5 opacity-40"}`}>
                    <Flame className="w-4 h-4" style={{ color: earned ? b.color : "#555" }} />
                    <div className="leading-tight">
                      <p className="text-xs font-semibold" style={{ color: earned ? b.color : "rgba(255,255,255,0.5)" }}>{b.label}</p>
                      <p className="text-[9px] uppercase tracking-[0.15em] text-white/30">{b.weeks}-week streak</p>
                    </div>
                    {earned && <span className="text-[10px] text-white/40">✓</span>}
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {totalSessions > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-3 gap-3 md:gap-4 mt-10"
            data-testid="session-stats"
          >
            {[
              { label: "Sessions", value: totalSessions, testid: "stat-total" },
              { label: "Avg rating", value: avgRating ? `${avgRating}` : "—", suffix: avgRating ? "/10" : "", testid: "stat-avg" },
              { label: "Latest", value: latestRating != null ? `${latestRating}` : "—", suffix: latestRating != null ? "/10" : "", testid: "stat-latest" },
            ].map((s) => (
              <div key={s.label} data-testid={s.testid} className="border border-white/10 p-5 md:p-6">
                <p className="text-xs uppercase tracking-[0.2em] text-white/40">{s.label}</p>
                <p className="font-display font-bold text-4xl md:text-5xl mt-2 text-[#D4AF37]">
                  {s.value}<span className="text-white/30 text-lg md:text-xl">{s.suffix || ""}</span>
                </p>
              </div>
            ))}
          </motion.div>
        )}

        {chartData.length >= 2 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-12 border border-white/10 p-6 md:p-8"
            data-testid="progress-chart"
          >
            <p className="text-xs uppercase tracking-[0.25em] text-white/40 mb-6">Rating over time</p>
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} />
                  <YAxis domain={[0, 10]} ticks={[0, 2, 4, 6, 8, 10]} tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ background: "#141414", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 0, color: "#fff" }}
                    labelStyle={{ color: "#D4AF37" }} cursor={{ stroke: "rgba(212,175,55,0.3)" }} />
                  <Line type="monotone" dataKey="rating" stroke="#D4AF37" strokeWidth={2.5}
                    dot={{ fill: "#D4AF37", r: 4 }} activeDot={{ r: 6, fill: "#2E6BE6" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
        )}

        {idp && (IDP_FIELDS.some((f) => idp[f.key]) || idp.review_date) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-12 border border-white/10 p-6 md:p-8"
            data-testid="idp-card"
          >
            <p className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/40 mb-6">
              <Target className="w-4 h-4 text-[#D4AF37]" /> Your Development Plan
            </p>
            <div className="grid md:grid-cols-2 gap-x-10 gap-y-6">
              {IDP_FIELDS.filter((f) => idp[f.key]).map((f) => (
                <div key={f.key}>
                  <p className="text-[#2E6BE6] text-xs uppercase tracking-[0.2em]">{f.label}</p>
                  <p className="text-white/80 mt-2 whitespace-pre-wrap text-sm leading-relaxed">{idp[f.key]}</p>
                </div>
              ))}
            </div>
            {idp.review_date && (
              <p className="text-white/40 text-xs uppercase tracking-[0.2em] mt-8 border-t border-white/10 pt-5">
                Next review: <span className="text-[#D4AF37]">{idp.review_date}</span>
              </p>
            )}
          </motion.div>
        )}

        {skills.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-12 border border-white/10 p-6 md:p-8"
            data-testid="skills-card"
          >
            <p className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/40 mb-2">
              <Target className="w-4 h-4 text-[#D4AF37]" /> Your Skill Wheel
            </p>
            <p className="text-white/40 text-xs mb-6">Each spoke is your average across that category · 0 = developing → {maxRating} = fluent.</p>

            <div className="grid lg:grid-cols-2 gap-8 items-start">
              <SkillWheel skills={skills} categories={skillCats} maxRating={maxRating}
                selected={selectedCat} onSelect={(c) => setSelectedCat(c === selectedCat ? null : c)} />

              <div data-testid="skill-drilldown-panel">
                {!selectedCat ? (
                  <div className="border border-white/10 p-5 h-full">
                    <p className="text-xs uppercase tracking-[0.25em] text-white/40 mb-4">Category breakdown</p>
                    <div className="space-y-3">
                      {skillCats.map((cat) => {
                        const list = skills.filter((s) => s.category === cat);
                        const rated = list.filter((s) => s.rating > 0);
                        const avg = rated.length ? (rated.reduce((a, s) => a + s.rating, 0) / rated.length) : 0;
                        return (
                          <button key={cat} onClick={() => setSelectedCat(cat)}
                            data-testid={`skill-cat-row-${cat}`}
                            className="w-full flex items-center gap-3 text-left group">
                            <span className="text-sm text-white/80 group-hover:text-white flex-1 truncate">{cat}</span>
                            <div className="w-24 h-1.5 bg-white/10 shrink-0">
                              <div className="h-full bg-[#D4AF37]" style={{ width: `${(avg / maxRating) * 100}%` }} />
                            </div>
                            <span className="text-xs font-display font-bold text-[#D4AF37] w-10 text-right">{avg.toFixed(1)}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="border border-[#D4AF37]/30 p-5" data-testid="skill-drilldown-active">
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-[#D4AF37] text-xs uppercase tracking-[0.25em] font-bold">{selectedCat}</p>
                      <button onClick={() => setSelectedCat(null)} data-testid="skill-drilldown-close"
                        className="text-white/40 hover:text-white text-xs uppercase tracking-[0.15em]">← All</button>
                    </div>
                    <div className="space-y-4 max-h-[420px] overflow-y-auto pr-2">
                      {skills.filter((s) => s.category === selectedCat).sort((a, b) => a.rating - b.rating).map((s, i) => (
                        <div key={i} data-testid={`skill-view-${i}`}>
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-sm text-white/85">{s.name}</span>
                            <span className="text-xs font-display font-bold text-[#D4AF37]">{s.rating}<span className="text-white/30">/{maxRating}</span></span>
                          </div>
                          <div className="flex gap-1">
                            {Array.from({ length: maxRating }, (_, n) => (
                              <span key={n} className={`h-1.5 flex-1 ${n < s.rating ? "bg-[#D4AF37]" : "bg-white/10"}`} />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {skillProgress.length >= 2 && (
              <div className="mt-8 border-t border-white/10 pt-6" data-testid="skill-progress">
                <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-4">Overall progress (avg /{maxRating})</p>
                <div style={{ width: "100%", height: 200 }}>
                  <ResponsiveContainer>
                    <LineChart data={skillProgress} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                      <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                      <XAxis dataKey="date" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} />
                      <YAxis domain={[0, maxRating]} tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} tickLine={false} axisLine={false} />
                      <Tooltip contentStyle={{ background: "#141414", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 0, color: "#fff" }} labelStyle={{ color: "#D4AF37" }} />
                      <Line type="monotone" dataKey="avg" stroke="#2E6BE6" strokeWidth={2.5} dot={{ fill: "#2E6BE6", r: 4 }} activeDot={{ r: 6, fill: "#D4AF37" }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {fitness.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-12 border border-white/10 p-6 md:p-8"
            data-testid="fitness-card"
          >
            <p className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/40 mb-6">
              <Activity className="w-4 h-4 text-[#2E6BE6]" /> Fitness Testing
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(fitness.reduce((acc, e) => { (acc[e.metric] = acc[e.metric] || []).push(e); return acc; }, {})).map(([metric, entries]) => {
                const sorted = [...entries].sort((a, b) => (a.date > b.date ? 1 : -1));
                const latest = sorted[sorted.length - 1];
                const prev = sorted.length > 1 ? sorted[sorted.length - 2] : null;
                const delta = prev ? (latest.value - prev.value) : null;
                const spark = sorted.map((e) => ({ date: e.date, v: e.value }));
                return (
                  <div key={metric} className="border border-white/10 p-4" data-testid={`fitness-metric-card-${metric}`}>
                    <p className="text-white/50 text-xs uppercase tracking-[0.15em]">{metric}</p>
                    <div className="flex items-end gap-2 mt-2">
                      <span className="font-display font-bold text-3xl text-white">{latest.value}</span>
                      <span className="text-white/40 text-sm mb-1">{latest.unit}</span>
                      {delta !== null && (
                        <span className={`text-xs mb-1.5 ml-auto ${delta >= 0 ? "text-green-400" : "text-red-400"}`}>
                          {delta >= 0 ? "▲" : "▼"} {Math.abs(delta).toFixed(2)}
                        </span>
                      )}
                    </div>
                    {spark.length >= 2 && (
                      <div style={{ width: "100%", height: 44 }} className="mt-2">
                        <ResponsiveContainer>
                          <AreaChart data={spark} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                            <defs>
                              <linearGradient id={`fg-${metric.replace(/\W/g, "")}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#2E6BE6" stopOpacity="0.5" />
                                <stop offset="100%" stopColor="#2E6BE6" stopOpacity="0" />
                              </linearGradient>
                            </defs>
                            <Area type="monotone" dataKey="v" stroke="#2E6BE6" strokeWidth={2} fill={`url(#fg-${metric.replace(/\W/g, "")})`} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                    <p className="text-white/30 text-[10px] mt-1">Last tested {latest.date}</p>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        <div className="mt-12">
          {loading ? (
            <div className="flex items-center gap-2 text-white/50"><Loader2 className="w-4 h-4 animate-spin" /> Loading reports…</div>
          ) : reports.length === 0 ? (
            <div className="border border-dashed border-white/15 py-20 flex flex-col items-center text-white/40" data-testid="reports-empty">
              <FileText className="w-10 h-10 mb-4" />
              <p className="text-sm">No reports yet. They'll appear here after your sessions.</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="reports-list">
              {reports.map((r) => (
                <motion.div
                  key={r.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="border border-white/10 p-6 md:p-8"
                  data-testid={`report-${r.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-white/40">Session</p>
                      <p className="font-display font-bold text-2xl mt-1">{r.date}</p>
                    </div>
                    {typeof r.rating === "number" && (
                      <div className="flex items-center gap-1 text-[#D4AF37]" title={`${r.rating}/10`}>
                        <Star className="w-4 h-4 fill-[#D4AF37]" />
                        <span className="font-display font-bold text-xl">{r.rating}<span className="text-white/40 text-sm">/10</span></span>
                      </div>
                    )}
                  </div>
                  {r.focus_areas?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {r.focus_areas.map((f, i) => (
                        <span key={i} className="text-xs uppercase tracking-[0.15em] border border-white/15 px-3 py-1 text-white/70">{f}</span>
                      ))}
                    </div>
                  )}
                  <p className="text-white/70 mt-5 whitespace-pre-wrap font-body leading-relaxed">{r.notes}</p>
                  {r.has_attachment && (
                    <>
                      <p className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-white/40 mt-5">
                        <Paperclip className="w-3.5 h-3.5" /> Attachment
                      </p>
                      <Attachment reportId={r.id} type={r.attachment_type} token={token} />
                    </>
                  )}
                </motion.div>
              ))}
            </div>
          )}

        <Reschedule token={token} />

        </div>
      </div>
    </div>
  );
}
